import { getAdIdentity } from "@/lib/analytics";
import { aiApiClient, apiClient } from "../config/api-client";
import { API_ENDPOINTS } from "../config/api-endpoints";
import {
  ActiveCoupon,
  AddAddressPayload,
  AddAddressResponse,
  AIResponse,
  APIRequestParams,
  BackendConversationMessagesResponse,
  CancelSubscriptionResponse,
  ComparePricingPlanResponse,
  CouponArea,
  GetActiveCouponResponse,
  ChangeConversationNameRequest,
  ChangeConversationNameResponse,
  CheckoutSessionStatusResponse,
  ConversationsListParams,
  ConversationsListResponse,
  CreateCheckoutSessionRequest,
  CreateCheckoutSessionResponse,
  CreateConversationRequest,
  CreateConversationResponse,
  DeleteAddressResponse,
  DeleteConversationResponse,
  GetAddressesResponse,
  UpdateAddressPayload,
  UpdateAddressResponse,
  // 🎯 Widget System API Types
  EnhancePromptRequest,
  EnhancePromptResponse,
  // 🎙️ Dictation API Types
  TranscribeAudioResponse,
  TranscribeTextData,
  ImageGenerationRequest,
  ImageGenerationResponse,
  AddMessageRequest,
  AddMessageResponse,
  // 🛒 Shop/Cart API Types
  AddToCartRequest,
  AddToCartResponse,
  UpdateCartQuantityRequest,
  UpdateCartQuantityResponse,
  UpdateCartSelectionRequest,
  UpdateCartSelectionResponse,
  GetCartResponse,
  DeleteFromCartResponse,
  // Brand API Types
  GetBrandByBrandIdResponse,
  CardInfoResponse,
  CheckoutAgreementsRequest,
  CheckoutAgreementsResponse,
  // Checkout API Types
  CheckoutRequest,
  CheckoutResponse,
  CheckoutStatusResponse,
  SavedCardsResponse,
  // Orders API Types
  OrderGroupResponse,
  OrderListParams,
  OrderListResponse,
  // Site Config API Types
  SiteConfigResponse,
  // Billing History API Types
  BillingHistoryResponse,
  // Customer Portal API Types
  CustomerPortalRequest,
  CustomerPortalResponse,
  // Profile Update API Types
  UpdateProfileRequest,
  UpdateProfileResponse,
  // Image Upload API Types
  UploadImageResponse,
  // Try-On Photos API Types
  AddTryOnPhotoRequest,
  TryOnPhotoResponse,
  TryOnPhotosResponse,
  UpdateTryOnPhotoRequest,
  // Notification API Types
  UpdateNotificationRequest,
  // 🔐 Change Password API Types
  ChangePasswordRequest,
  ChangePasswordResponse,
  // 📊 Token Usage History API Types
  TokenUsageHistoryResponse,
  TokenUsageHistoryParams,
  TokenUsageRunHistoryResponse,
  TokenUsageRunHistoryParams,
  // 📝 Agreement API Types
  AgreementResponse,
  ConfirmAgreementResponse,
  // 🎯 Prompt Enhancement API Types
  PromptEnhanceRequest,
  PromptEnhanceResponse,
  PromptTranslateRequest,
  PromptTranslateResponse,
  // 🛍️ E-commerce Category API Types
  MainCategoriesResponse,
  SubCategoriesResponse,
  // 🔗 Chat Share Link API Types
  GetShareLinkRequest,
  GetShareLinkResponse,
  RemoveShareTokenResponse,
  MessageFeedbackRequest,
  MessageFeedbackResponse,
  // 🛍️ E-commerce Seller API Types
  PopularSellersResponse,
  // 🛍️ E-commerce Featured Products API Types
  FeaturedProductsResponse,
  // 📍 Location API Types
  GetCountriesResponse,
  GetCitiesResponse,
  GetTownsResponse,
  GetQuartersResponse,
  LocationParams,
  // 🎨 Preferences API Types
  CategoryPreferencesResponse,
  UserPreferencesResponse,
  UpdatePreferencesRequest,
  // 🎁 Rewards API Types
  RewardsResponse,
  RewardHistoryResponse,
  RewardHistoryParams,
  ConvertRewardsRequest,
  ConvertRewardsResponse,
  PreviewConvertRewardsRequest,
  PreviewConvertRewardsResponse,
  // 🛫 Cosmopoints API Types
  CosmopointsProgramListResponse,
  CosmopointsConnectionListResponse,
  CosmopointsConnectionResponse,
  CreateCosmopointsConnectionRequest,
  // 🏪 Checkout Approvals API Types
  CheckoutApprovalsResponse,
  SubmitCheckoutApprovalsRequest,
} from "../models/api-types";

// Basit bir önbellek uygulaması
class QueryCache {
  private cache: Map<string, { data: AIResponse; timestamp: number }> =
    new Map();
  private readonly MAX_CACHE_SIZE = 50; // Maksimum önbellek boyutu
  private readonly CACHE_TTL = 10 * 60 * 1000; // 10 dakika önbellek süresi

  set(key: string, data: AIResponse): void {
    // Önbellek dolu ise en eski girdiyi kaldır
    if (this.cache.size >= this.MAX_CACHE_SIZE) {
      const oldestKey = this.getOldestKey();
      if (oldestKey) this.cache.delete(oldestKey);
    }

    // Yeni veriyi ekle
    this.cache.set(key, { data, timestamp: Date.now() });
  }

  get(key: string): AIResponse | null {
    const cached = this.cache.get(key);

    // Önbellekte yoksa veya süresi dolmuşsa null döndür
    if (!cached || Date.now() - cached.timestamp > this.CACHE_TTL) {
      if (cached) this.cache.delete(key); // Süresi dolmuş girdiyi temizle
      return null;
    }

    return cached.data;
  }

  private getOldestKey(): string | null {
    let oldestKey: string | null = null;
    let oldestTime = Date.now();

    for (const [key, { timestamp }] of this.cache.entries()) {
      if (timestamp < oldestTime) {
        oldestTime = timestamp;
        oldestKey = key;
      }
    }

    return oldestKey;
  }

  // Önbelleği temizle
  clear(): void {
    this.cache.clear();
  }
}

/**
 * api-client 4xx'i reject etmez; vision reddi de
 * `{ code, message }` olarak resolve edilir (success alanı olmayabilir).
 */
function assertTryOnSuccess(response: {
  success?: boolean;
  data?: unknown;
  code?: string;
}): void {
  const failed =
    response?.success === false ||
    typeof response?.code === "string" ||
    response?.data == null;

  if (failed) {
    throw response;
  }
}

export class QueryService {
  private cache = new QueryCache();

  async sendQuery(params: APIRequestParams): Promise<AIResponse> {
    try {
      // Yeni /chat endpoint'i JSON body bekliyor
      const payload: any = {
        model: params.model,
        message: params.message ?? params.prompt,
        chat_id: params.chat_id,
        stream: false,
      };

      // Artık user_id gönderilmiyor (auth'dan alınıyor)
      // Ek alanlar (type, message_id) yeni endpoint'te kullanılmıyor, ignore

      // /chat çağrısı AI base + /api/v1 prefiksi ile gönderilmeli
      const response = await aiApiClient.post<any>(API_ENDPOINTS.ask, payload);

      // Yeni response şemasını dönüştür
      const transformedResponse: AIResponse =
        this.transformChatResponse(response);

      return transformedResponse;
    } catch (error) {
      console.error("API error:", error);
      throw error;
    }
  }

  async getConversations(
    params: ConversationsListParams = {}
  ): Promise<ConversationsListResponse> {
    try {
      const queryParams = new URLSearchParams();

      if (params.page !== undefined)
        queryParams.append("page", params.page.toString());
      if (params.limit !== undefined)
        queryParams.append("limit", params.limit.toString());
      if (params.sort) queryParams.append("sort", params.sort);
      if (params.sortBy) queryParams.append("sortBy", params.sortBy);
      // API search parametresi kullan (searchTerm yerine)
      if (params.search) {
        queryParams.append("search", params.search);
      } else if (params.searchTerm) {
        // Geriye dönük uyumluluk için searchTerm'i search'e çevir
        queryParams.append("search", params.searchTerm);
      }
      if (params.module) queryParams.append("module", params.module);

      const url = `${API_ENDPOINTS.getConversations}${
        queryParams.toString() ? `?${queryParams.toString()}` : ""
      }`;

      const response = await apiClient.get<ConversationsListResponse>(url);

      // Backend'den dönen response'u kontrol et

      return response;
    } catch (error) {
      throw error;
    }
  }

  async changeConversationName(
    conversationId: string,
    newName: string
  ): Promise<ChangeConversationNameResponse> {
    try {
      const requestData: ChangeConversationNameRequest = {
        name: newName,
      };

      const response = await apiClient.patch<ChangeConversationNameResponse>(
        API_ENDPOINTS.changeConversationName(conversationId),
        requestData
      );

      return response;
    } catch (error) {
      console.error("Change conversation name error:", error);
      throw error;
    }
  }

  async getShareLink(
    conversationId: string
  ): Promise<GetShareLinkResponse> {
    try {
      const requestData: GetShareLinkRequest = {
        conversationId,
      };

      const response = await apiClient.post<GetShareLinkResponse>(
        API_ENDPOINTS.getShareLink,
        requestData
      );

      return response;
    } catch (apiError: any) {
      console.error("Get share link error:", apiError);
      throw apiError;
    }
  }

  async removeShareToken(
    conversationId: string
  ): Promise<RemoveShareTokenResponse> {
    try {
      const response = await apiClient.patch<RemoveShareTokenResponse>(
        API_ENDPOINTS.removeShareToken(conversationId),
        {}
      );

      return response;
    } catch (apiError: any) {
      console.error("Remove share token error:", apiError);
      throw apiError;
    }
  }

  async getConversationMessages(
    conversationId: string
  ): Promise<BackendConversationMessagesResponse> {
    try {
      const response = await apiClient.get<BackendConversationMessagesResponse>(
        API_ENDPOINTS.getMessagesFromConversation(conversationId)
      );

      return response;
    } catch (error) {
      console.error("Get conversation messages error:", error);
      throw error;
    }
  }

  async updateMessageFeedback(
    request: MessageFeedbackRequest
  ): Promise<MessageFeedbackResponse> {
    try {
      const response = await apiClient.patch<MessageFeedbackResponse>(
        API_ENDPOINTS.updateMessageFeedback,
        request
      );
      return response;
    } catch (error) {
      console.error("❌ Update message feedback API error:", error);
      throw error;
    }
  }

  async createConversation(
    platform: string,
    initialMessage?: string,
    isTemporary?: boolean,
    module?: string,
    agentId?: string
  ): Promise<CreateConversationResponse> {
    try {
      const requestData: CreateConversationRequest = {
        platform,
        ...(initialMessage && { initialMessage }),
        ...(isTemporary && { isTemporary }),
        ...(module && { module }),
        ...(agentId && { agentId }),
      };

      const response = await apiClient.post<CreateConversationResponse>(
        API_ENDPOINTS.createConversation,
        requestData
      );

      return response;
    } catch (error) {
      console.error("Create conversation error:", error);
      throw error;
    }
  }

  async deleteConversation(
    conversationId: string
  ): Promise<DeleteConversationResponse> {
    try {
      const response = await apiClient.delete<DeleteConversationResponse>(
        API_ENDPOINTS.deleteConversation(conversationId)
      );

      return response;
    } catch (error) {
      console.error("Delete conversation error:", error);
      throw error;
    }
  }

  private transformChatResponse(apiResponse: any): AIResponse {
    // aiApiClient.post geri dönüşü Axios response.data'dır (unwrap edilmiş)
    // Beklenen şekil: { id, task, content?, model?, modelInfo?, data?: { content?, model?, modelInfo?, models? } }
    const top = apiResponse ?? {};
    const root = top?.data ?? {};
    const nested = root?.data ?? {};

    const content = top?.content ?? root?.content ?? nested?.content ?? "";
    const model = top?.model ?? root?.model ?? nested?.model ?? null;
    const modelInfo =
      top?.modelInfo ?? root?.modelInfo ?? nested?.modelInfo ?? null;
    const task = top?.task ?? root?.task ?? nested?.task ?? undefined;

    // CREATIVE_GENERATION için modeller farklı yerlerde gelebilir
    const modelsFromNested = Array.isArray(nested?.models)
      ? (nested.models as any[])
      : undefined;
    const modelsFromRoot = Array.isArray(root?.models)
      ? (root.models as any[])
      : undefined;
    const modelsFromTop = Array.isArray(top?.models)
      ? (top.models as any[])
      : undefined;

    const creativeModels =
      modelsFromTop || modelsFromRoot || modelsFromNested || undefined;

    return {
      id: top?.id ?? root?.id ?? nested?.id ?? undefined,
      message: typeof content === "string" ? content : JSON.stringify(content),
      model: modelInfo?.modelName || model,
      modelInfo,
      task,
      creativeModels,
    };
  }

  // Önbelleği temizle
  clearCache(): void {
    this.cache.clear();
  }

  // Adresleri getir
  async getAddresses(): Promise<GetAddressesResponse> {
    try {
      const response = await apiClient.get<GetAddressesResponse>(
        API_ENDPOINTS.getAddresses
      );
      return response;
    } catch (error) {
      console.error("Get addresses error:", error);
      throw error;
    }
  }

  // Adres ekle
  async addAddress(payload: AddAddressPayload): Promise<AddAddressResponse> {
    try {
      const response = await apiClient.post<AddAddressResponse>(
        API_ENDPOINTS.addAddress,
        payload
      );
      return response;
    } catch (error) {
      console.error("Add address error:", error);
      throw error;
    }
  }

  // Adres güncelle
  async updateAddress(
    id: string,
    payload: UpdateAddressPayload
  ): Promise<UpdateAddressResponse> {
    try {
      const response = await apiClient.put<UpdateAddressResponse>(
        API_ENDPOINTS.updateAddress(id),
        payload
      );
      return response;
    } catch (error) {
      console.error("Update address error:", error);
      throw error;
    }
  }

  // Adres sil (PUT method kullanır)
  async deleteAddress(id: string): Promise<DeleteAddressResponse> {
    try {
      const response = await apiClient.put<DeleteAddressResponse>(
        API_ENDPOINTS.deleteAddress(id)
      );
      return response;
    } catch (error) {
      console.error("Delete address error:", error);
      throw error;
    }
  }

  // 📍 Location API Methods
  async getLocation(
    params: LocationParams
  ): Promise<
    | GetCountriesResponse
    | GetCitiesResponse
    | GetTownsResponse
    | GetQuartersResponse
  > {
    try {
      const queryParams: Record<string, string | number> = {
        type: params.type,
      };

      if (params.countryId !== undefined) {
        queryParams.countryId = params.countryId;
      }

      if (params.cityId !== undefined) {
        queryParams.cityId = params.cityId;
      }

      if (params.townId !== undefined) {
        queryParams.townId = params.townId;
      }

      const response = await apiClient.get<
        | GetCountriesResponse
        | GetCitiesResponse
        | GetTownsResponse
        | GetQuartersResponse
      >(API_ENDPOINTS.getLocation, { params: queryParams });
      return response;
    } catch (error) {
      console.error("Get location error:", error);
      throw error;
    }
  }

  // 🎯 Widget System API Methods
  async enhancePrompt(
    request: EnhancePromptRequest
  ): Promise<EnhancePromptResponse> {
    try {
      const response = await aiApiClient.post<EnhancePromptResponse>(
        API_ENDPOINTS.enhancePrompt,
        request
      );
      return response;
    } catch (error) {
      console.error("❌ Enhance prompt API error:", error);
      throw new Error("Failed to enhance prompt");
    }
  }

  async generateImage(
    request: ImageGenerationRequest
  ): Promise<ImageGenerationResponse> {
    try {
      const response = await aiApiClient.post<ImageGenerationResponse>(
        API_ENDPOINTS.generateImage,
        request
      );

      return response;
    } catch (error) {
      console.error("❌ Image generation API error:", error);
      throw new Error("Failed to generate image");
    }
  }

  async addMessage(request: AddMessageRequest): Promise<AddMessageResponse> {
    try {
      const response = await apiClient.post<AddMessageResponse>(
        API_ENDPOINTS.addMessage,
        request
      );
      return response;
    } catch (error) {
      console.error("❌ Add message API error:", error);
      throw error;
    }
  }

  async createCheckoutSession(
    pricingPlanId: string,
    email: string,
    userId: string
  ): Promise<CreateCheckoutSessionResponse> {
    try {
      const requestData: CreateCheckoutSessionRequest = {
        pricingPlanId,
        email,
        userId,
        // Reklam kimliği çağıranın kararı değil, tarayıcının bir özelliği.
        // Tek HTTP sınırında eklendiği için hiçbir çağrı noktası unutamaz.
        attribution: getAdIdentity(),
      };

      const response = await apiClient.post<CreateCheckoutSessionResponse>(
        API_ENDPOINTS.createCheckoutSession,
        requestData
      );

      return response;
    } catch (error) {
      console.error("Create conversation error:", error);
      throw error;
    }
  }

  async getCheckoutSessionStatus(
    sessionId: string
  ): Promise<CheckoutSessionStatusResponse> {
    try {
      const response = await apiClient.get<{
        success: boolean;
        data: CheckoutSessionStatusResponse;
      }>(API_ENDPOINTS.checkoutSessionStatus(sessionId));

      return response.data;
    } catch (error) {
      console.error("Get checkout session status error:", error);
      throw error;
    }
  }

  // 🛒 Shop/Cart API Methods
  async addToCart(request: AddToCartRequest): Promise<AddToCartResponse> {
    try {
      const response = await apiClient.post<AddToCartResponse>(
        API_ENDPOINTS.addToCart,
        request,
      );

      // apiClient 4xx cevaplarını da resolve ettiği için burada success kontrolü zorunlu.
      if (!response?.success) {
        const apiError = new Error(
          response?.message || "Add to cart request failed",
        );
        (apiError as any).response = { data: response };
        throw apiError;
      }

      return response;
    } catch (apiError) {
      console.error("❌ Add to cart API error:", apiError);
      throw apiError;
    }
  }

  async updateCartQuantity(
    cartId: string,
    request: UpdateCartQuantityRequest
  ): Promise<UpdateCartQuantityResponse> {
    try {
      const response = await apiClient.put<UpdateCartQuantityResponse>(
        API_ENDPOINTS.updateCartQuantity(cartId),
        request
      );
      return response;
    } catch (error) {
      console.error("❌ Update cart quantity API error:", error);
      throw error;
    }
  }

  // Update cart item selection (select/unselect one or more cart items)
  async updateCartSelection(
    request: UpdateCartSelectionRequest,
  ): Promise<UpdateCartSelectionResponse> {
    try {
      const response = await apiClient.put<UpdateCartSelectionResponse>(
        API_ENDPOINTS.cartSelection,
        request,
      );
      return response;
    } catch (error) {
      console.error("❌ Update cart selection API error:", error);
      throw error;
    }
  }

  // Get Cart (Updated: Now returns hierarchical structure with merchants)
  // offerId varsa sadece o teklife ait ürünü döner (hemen al akışı)
  // quantity ile birlikte kullanılırsa belirlenen adet kadar döner
  async getAllCart(offerId?: string, quantity?: number): Promise<GetCartResponse> {
    try {
      const baseUrl = API_ENDPOINTS.getAllCart;
      let url: string = baseUrl;
      if (offerId) {
        const params = new URLSearchParams({ offerId });
        if (quantity && quantity > 1) params.set("quantity", String(quantity));
        url = `${baseUrl}?${params.toString()}`;
      }
      const response = await apiClient.get<GetCartResponse>(url as typeof baseUrl);
      return response;
    } catch (error) {
      console.error("❌ Get cart API error:", error);
      throw error;
    }
  }

  async deleteFromCart(cartId: string): Promise<DeleteFromCartResponse> {
    try {
      const response = await apiClient.delete<DeleteFromCartResponse>(
        API_ENDPOINTS.deleteFromCart(cartId)
      );
      return response;
    } catch (error) {
      console.error("❌ Delete from cart API error:", error);
      throw error;
    }
  }

  // 🛍️ E-commerce Brand API Methods
  async getBrandByBrandId(
    id: string | number
  ): Promise<GetBrandByBrandIdResponse> {
    try {
      const response = await apiClient.get<GetBrandByBrandIdResponse>(
        API_ENDPOINTS.getBrandByBrandId(String(id))
      );
      return response;
    } catch (error) {
      console.error("❌ Get brand by brandId API error:", error);
      throw error;
    }
  }

  // 🧾 Checkout - Card Info by BIN
  async getCardInfoByBin(
    binNumber: string,
    buyNow?: { offerId: string; quantity: number }
  ): Promise<CardInfoResponse> {
    try {
      const payload: Record<string, unknown> = { binNumber };
      if (buyNow) payload.buyNow = buyNow;
      const response = await apiClient.post<CardInfoResponse>(
        API_ENDPOINTS.checkoutCardInfo,
        payload
      );
      return response;
    } catch (error) {
      console.error("❌ Get card info by BIN API error:", error);
      throw error;
    }
  }

  // 🧾 Checkout - Agreements
  async getCheckoutAgreements(
    request: CheckoutAgreementsRequest,
    offerId?: string
  ): Promise<CheckoutAgreementsResponse> {
    try {
      const baseUrl = API_ENDPOINTS.checkoutAgreements;
      const url = offerId
        ? `${baseUrl}?offerId=${encodeURIComponent(offerId)}`
        : baseUrl;
      const response = await apiClient.post<CheckoutAgreementsResponse>(url, request);
      return response;
    } catch (error) {
      console.error("❌ Checkout agreements API error:", error);
      throw error;
    }
  }

  // 🧾 Checkout - Complete Order
  async checkout(request: CheckoutRequest): Promise<CheckoutResponse> {
    try {
      const response = await apiClient.post<CheckoutResponse>(
        API_ENDPOINTS.checkout,
        request
      );
      return response;
    } catch (error) {
      console.error("❌ Checkout API error:", error);
      throw error;
    }
  }

  // 🏪 Checkout - Merchant Approvals (GET)
  async getCheckoutApprovals(): Promise<CheckoutApprovalsResponse> {
    try {
      const response = await apiClient.get<CheckoutApprovalsResponse>(
        API_ENDPOINTS.checkoutApprovals
      );
      return response;
    } catch (apiError) {
      console.error("❌ Get checkout approvals API error:", apiError);
      throw apiError;
    }
  }

  // 🏪 Checkout - Submit Merchant Approvals (PUT)
  async submitCheckoutApprovals(
    request: SubmitCheckoutApprovalsRequest
  ): Promise<unknown> {
    try {
      const response = await apiClient.put<unknown>(
        API_ENDPOINTS.checkoutApprovals,
        request
      );
      return response;
    } catch (apiError) {
      console.error("❌ Submit checkout approvals API error:", apiError);
      throw apiError;
    }
  }

  // 🔄 Checkout - Order Processing Status (polling)
  async getCheckoutStatus(orderGroupId: string): Promise<CheckoutStatusResponse> {
    try {
      const response = await apiClient.get<CheckoutStatusResponse>(
        API_ENDPOINTS.checkoutStatus(orderGroupId)
      );
      return response;
    } catch (error) {
      console.error("❌ Checkout status error:", error);
      throw error;
    }
  }

  // 💳 Saklı Kart API Methods
  async getSavedCards(): Promise<SavedCardsResponse> {
    try {
      const response = await apiClient.get<SavedCardsResponse>(
        API_ENDPOINTS.getCards
      );
      return response;
    } catch (error) {
      console.error("❌ Get saved cards API error:", error);
      throw error;
    }
  }

  async addCard(payload: import("../models/api-types").AddCardPayload): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.post<{ success: boolean; message: string }>(
        API_ENDPOINTS.getCards,
        payload
      );
      return response;
    } catch (error) {
      console.error("❌ Add card API error:", error);
      throw error;
    }
  }

  async deleteCard(cardToken: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.delete<{ success: boolean; message: string }>(
        API_ENDPOINTS.deleteCard(cardToken)
      );
      return response;
    } catch (error) {
      console.error("❌ Delete card API error:", error);
      throw error;
    }
  }

  // 🎯 Site Config API Methods
  async getSiteConfigs(): Promise<SiteConfigResponse> {
    try {
      const response = await apiClient.get<SiteConfigResponse>(
        API_ENDPOINTS.getSiteConfigs
      );
      return response;
    } catch (error) {
      console.error("❌ Get site configs API error:", error);
      throw error;
    }
  }

  // 💳 Billing History API Methods
  async getBillingHistory(): Promise<BillingHistoryResponse> {
    try {
      const response = await apiClient.get<BillingHistoryResponse>(
        API_ENDPOINTS.getBillingHistory
      );
      return response;
    } catch (error) {
      console.error("❌ Get billing history API error:", error);
      throw error;
    }
  }

  async cancelSubscription(
    reason: string
  ): Promise<CancelSubscriptionResponse> {
    try {
      const response = await apiClient.post<CancelSubscriptionResponse>(
        API_ENDPOINTS.cancelSubscription,
        { reason }
      );
      return response;
    } catch (error) {
      console.error("❌ Cancel subscription API error:", error);
      throw error;
    }
  }

  async getCustomerPortalUrl(
    subscriptionId: string,
    targetPricingPlanId: string
  ): Promise<CustomerPortalResponse> {
    try {
      const requestData: CustomerPortalRequest = {
        type: "subscription_update_confirm",
        subscriptionId,
        targetPricingPlanId,
      };
      const response = await apiClient.post<CustomerPortalResponse>(
        API_ENDPOINTS.customerPortal,
        requestData
      );
      return response;
    } catch (error) {
      console.error("❌ Customer portal API error:", error);
      throw error;
    }
  }

  async getPaymentMethodUpdateUrl(): Promise<CustomerPortalResponse> {
    try {
      const requestData: CustomerPortalRequest = {
        type: "payment_method_update",
      };
      const response = await apiClient.post<CustomerPortalResponse>(
        API_ENDPOINTS.customerPortal,
        requestData
      );
      return response;
    } catch (error) {
      console.error("❌ Payment method update API error:", error);
      throw error;
    }
  }

  async comparePricingPlan(): Promise<ComparePricingPlanResponse> {
    try {
      const response = await apiClient.post<ComparePricingPlanResponse>(
        API_ENDPOINTS.comparePricingPlan
      );
      return response;
    } catch (error) {
      console.error("❌ Compare pricing plan API error:", error);
      throw error;
    }
  }

  // Returns the active coupon for the given area if the user is eligible,
  // otherwise null. Never throws — returns null on any failure so callers
  // (e.g. the 402 handler) can safely fall back to the no-coupon display.
  async getActiveCoupon(area: CouponArea): Promise<ActiveCoupon | null> {
    try {
      const response = await apiClient.get<GetActiveCouponResponse>(
        API_ENDPOINTS.activeCoupon(area)
      );
      return response?.data ?? null;
    } catch (error) {
      console.error("❌ Get active coupon API error:", error);
      return null;
    }
  }

  // 👤 Profile Update API Methods
  async updateProfile(
    profileData: UpdateProfileRequest
  ): Promise<UpdateProfileResponse> {
    try {
      const response = await apiClient.put<UpdateProfileResponse>(
        API_ENDPOINTS.updateProfile,
        profileData
      );
      return response;
    } catch (error) {
      console.error("❌ Update profile API error:", error);
      throw error;
    }
  }

  // 🔐 Change Password API Methods
  async changePassword(
    passwordData: ChangePasswordRequest
  ): Promise<ChangePasswordResponse> {
    try {
      console.log("📡 Changing password...");
      const response = await apiClient.post<ChangePasswordResponse>(
        API_ENDPOINTS.changePassword,
        passwordData
      );
      return response;
    } catch (error) {
      console.error("❌ Change password API error:", error);
      throw error;
    }
  }

  // 📸 Image Upload API Methods
  async uploadImage(file: File): Promise<UploadImageResponse> {
    try {
      console.log("📡 Uploading image...", {
        name: file.name,
        size: file.size,
      });

      // Validate file size (max 8MB)
      const maxSize = 8 * 1024 * 1024; // 8MB in bytes
      if (file.size > maxSize) {
        throw new Error("File size exceeds 8MB limit");
      }

      // Validate file type
      if (!file.type.startsWith("image/")) {
        throw new Error("File must be an image");
      }

      const formData = new FormData();
      formData.append("file", file);

      const response = await apiClient.post<UploadImageResponse>(
        API_ENDPOINTS.uploadImage,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      console.log("✅ Image uploaded successfully");
      return response;
    } catch (error) {
      console.error("❌ Upload image API error:", error);
      throw error;
    }
  }

  // 👗 Try-On Photos API Methods
  async getTryOnPhotos(): Promise<TryOnPhotosResponse> {
    try {
      const response = await apiClient.get<TryOnPhotosResponse>(
        API_ENDPOINTS.getTryOnPhotos
      );
      return response;
    } catch (error) {
      console.error("❌ Get try-on photos API error:", error);
      throw error;
    }
  }

  async addTryOnPhoto(
    request: AddTryOnPhotoRequest
  ): Promise<TryOnPhotoResponse> {
    try {
      const formData = new FormData();
      formData.append("file", request.file);
      if (request.name) {
        formData.append("name", request.name);
      }

      const response = await apiClient.post<TryOnPhotoResponse>(
        API_ENDPOINTS.addTryOnPhoto,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      assertTryOnSuccess(response);
      return response;
    } catch (error) {
      console.error("❌ Add try-on photo API error:", error);
      throw error;
    }
  }

  async updateTryOnPhoto(
    photoId: string,
    updates: UpdateTryOnPhotoRequest
  ): Promise<TryOnPhotoResponse> {
    try {
      const response = await apiClient.patch<TryOnPhotoResponse>(
        API_ENDPOINTS.updateTryOnPhoto(photoId),
        updates
      );
      assertTryOnSuccess(response);
      return response;
    } catch (error) {
      console.error("❌ Update try-on photo API error:", error);
      throw error;
    }
  }

  async deleteTryOnPhoto(photoId: string): Promise<TryOnPhotosResponse> {
    try {
      const response = await apiClient.delete<TryOnPhotosResponse>(
        API_ENDPOINTS.deleteTryOnPhoto(photoId)
      );
      assertTryOnSuccess(response);
      return response;
    } catch (error) {
      console.error("❌ Delete try-on photo API error:", error);
      throw error;
    }
  }

  // 📊 Token Usage History API Methods
  async getTokenUsageHistoryByModel(
    params: TokenUsageHistoryParams = {}
  ): Promise<TokenUsageHistoryResponse> {
    try {
      console.log("📡 Fetching token usage history by model...");
      const queryParams = new URLSearchParams();
      if (params.page !== undefined)
        queryParams.append("page", String(params.page));
      if (params.limit !== undefined)
        queryParams.append("limit", String(params.limit));
      if (params.sortBy) queryParams.append("sortBy", params.sortBy);
      if (params.search) queryParams.append("search", params.search);

      const url = `${API_ENDPOINTS.getUserTokenUsageHistoryByModel}${
        queryParams.toString() ? `?${queryParams.toString()}` : ""
      }`;

      const response = await apiClient.get<TokenUsageHistoryResponse>(url);
      return response;
    } catch (error) {
      console.error("❌ Get token usage history by model API error:", error);
      throw error;
    }
  }

  async getUserTokenUsageHistory(
    params: TokenUsageRunHistoryParams = {}
  ): Promise<TokenUsageRunHistoryResponse> {
    try {
      console.log("📡 Fetching user token usage run history...");
      const queryParams = new URLSearchParams();

      if (params.page !== undefined) {
        queryParams.append("page", String(params.page));
      }
      if (params.limit !== undefined) {
        queryParams.append("limit", String(params.limit));
      }
      if (params.sortBy) {
        queryParams.append("sortBy", params.sortBy);
      }

      const url = `${API_ENDPOINTS.getUserTokenUsageHistory}${
        queryParams.toString() ? `?${queryParams.toString()}` : ""
      }`;

      const response = await apiClient.get<TokenUsageRunHistoryResponse>(url);
      return response;
    } catch (apiError) {
      console.error("❌ Get user token usage history API error:", apiError);
      throw apiError;
    }
  }

  // 🔔 Update Notification Settings
  async updateNotificationSettings(
    notificationData: UpdateNotificationRequest
  ): Promise<UpdateProfileResponse> {
    try {
      const response = await apiClient.put<UpdateProfileResponse>(
        API_ENDPOINTS.updateProfile,
        notificationData
      );
      return response;
    } catch (error) {
      console.error("❌ Update notification settings API error:", error);
      throw error;
    }
  }

  // 📝 Agreement API Methods
  async getAgreement(name: string): Promise<AgreementResponse> {
    try {
      console.log(`📡 Fetching agreement: ${name}...`);
      const response = await apiClient.get<AgreementResponse>(
        API_ENDPOINTS.getAgreement(name)
      );
      return response;
    } catch (error) {
      console.error(`❌ Get agreement (${name}) API error:`, error);
      throw error;
    }
  }

  async confirmAgreement(
    agreementName: string
  ): Promise<ConfirmAgreementResponse> {
    try {
      console.log(`📡 Confirming agreement: ${agreementName}...`);
      const response = await apiClient.post<ConfirmAgreementResponse>(
        API_ENDPOINTS.confirmAgreement,
        { agreementName }
      );
      return response;
    } catch (error) {
      console.error(
        `❌ Confirm agreement (${agreementName}) API error:`,
        error
      );
      throw error;
    }
  }

  // 🎯 Prompt Enhancement API Methods
  async enhancePromptText(
    request: PromptEnhanceRequest
  ): Promise<PromptEnhanceResponse> {
    try {
      console.log("📡 Enhancing prompt...");
      const response = await aiApiClient.post<{
        statusCode: number;
        success: boolean;
        data: PromptEnhanceResponse;
      }>(API_ENDPOINTS.promptEnhance, request);
      return response.data;
    } catch (apiError) {
      console.error("❌ Enhance prompt API error:", apiError);
      throw apiError;
    }
  }

  async translatePrompt(
    request: PromptTranslateRequest
  ): Promise<PromptTranslateResponse> {
    try {
      console.log("📡 Translating prompt...");
      const response = await aiApiClient.post<{
        statusCode: number;
        success: boolean;
        data: PromptTranslateResponse;
      }>(API_ENDPOINTS.promptTranslate, request);
      return response.data;
    } catch (apiError) {
      console.error("❌ Translate prompt API error:", apiError);
      throw apiError;
    }
  }

  // 🎙️ Dictation (Speech-to-Text) — POST /api/v1/transcribe/text
  // multipart: file + optional language; envelope { statusCode, success, data }
  async transcribeAudio(
    audioBlob: Blob,
    options?: { language?: string }
  ): Promise<TranscribeTextData> {
    try {
      const formData = new FormData();
      const extension =
        audioBlob.type.includes("mp4") || audioBlob.type.includes("m4a")
          ? "mp4"
          : "webm";
      formData.append(
        "file",
        audioBlob,
        `dictation-${Date.now()}.${extension}`
      );

      if (options?.language) {
        formData.append("language", options.language);
      }

      const response = await aiApiClient.post<TranscribeAudioResponse>(
        API_ENDPOINTS.transcribeAudio,
        formData
      );

      if (!response?.success || !response.data) {
        throw new Error(
          response?.message || "Transcribe request failed"
        );
      }

      return response.data;
    } catch (apiError) {
      console.error("❌ Transcribe audio API error:", apiError);
      throw apiError;
    }
  }

  // 🧾 Orders API Methods
  async getOrders(params?: OrderListParams): Promise<OrderListResponse> {
    try {
      console.log("📡 Fetching orders list...", params);
      const response = await apiClient.get<OrderListResponse>(
        API_ENDPOINTS.getOrders,
        { params }
      );
      return response;
    } catch (error) {
      console.error("❌ Get orders list API error:", error);
      throw error;
    }
  }

  async getOrderGroup(orderGroupId: string): Promise<OrderGroupResponse> {
    try {
      console.log("📡 Fetching order group...", orderGroupId);
      const response = await apiClient.get<OrderGroupResponse>(
        API_ENDPOINTS.getOrderGroup(orderGroupId)
      );
      return response;
    } catch (error) {
      console.error("❌ Get order group API error:", error);
      throw error;
    }
  }

  // 🛍️ E-commerce Category API Methods
  async getMainCategories(vertical?: string): Promise<MainCategoriesResponse> {
    try {
      console.log("📡 Fetching main categories...", vertical);
      const response = await apiClient.get<MainCategoriesResponse>(
        API_ENDPOINTS.getMainCategories,
        vertical ? { params: { vertical } } : undefined
      );
      return response;
    } catch (error) {
      console.error("❌ Get main categories API error:", error);
      throw error;
    }
  }

  async getSubCategoriesByMainCategory(
    mainCategoryName: string,
    locale?: string
  ): Promise<SubCategoriesResponse> {
    try {
      console.log("📡 Fetching sub categories...", mainCategoryName);
      const requestConfig = locale
        ? { headers: { "x-locale": locale } }
        : undefined;
      const response = await apiClient.get<SubCategoriesResponse>(
        API_ENDPOINTS.getSubCategoriesByMainCategory(mainCategoryName),
        requestConfig
      );
      return response;
    } catch (error) {
      console.error("❌ Get sub categories API error:", error);
      throw error;
    }
  }

  // 🛍️ E-commerce Seller API Methods
  async getPopularSellers(vertical?: string): Promise<PopularSellersResponse> {
    try {
      console.log("📡 Fetching popular sellers...", vertical);
      const response = await apiClient.get<PopularSellersResponse>(
        API_ENDPOINTS.getPopularSellers,
        vertical ? { params: { vertical } } : undefined
      );
      return response;
    } catch (error) {
      console.error("❌ Get popular sellers API error:", error);
      throw error;
    }
  }

  // 🛍️ E-commerce Featured Products API Methods
  async getFeaturedProducts(vertical?: string): Promise<FeaturedProductsResponse> {
    try {
      console.log("📡 Fetching featured products...", vertical);
      const response = await apiClient.get<FeaturedProductsResponse>(
        API_ENDPOINTS.getFeaturedProducts,
        vertical ? { params: { vertical } } : undefined
      );
      return response;
    } catch (error) {
      console.error("❌ Get featured products API error:", error);
      throw error;
    }
  }

  // 🎨 Preferences API Methods
  async getCategoryPreferences(
    country: string = "TR"
  ): Promise<CategoryPreferencesResponse> {
    try {
      console.log("📡 Fetching category preferences...");
      const response = await apiClient.get<CategoryPreferencesResponse>(
        API_ENDPOINTS.CATEGORY_PREFERENCES,
        { params: { country } }
      );
      return response;
    } catch (error) {
      console.error("❌ Get category preferences API error:", error);
      throw error;
    }
  }

  async getUserPreferences(): Promise<UserPreferencesResponse> {
    try {
      console.log("📡 Fetching user preferences...");
      const response = await apiClient.get<UserPreferencesResponse>(
        API_ENDPOINTS.USER_PREFERENCES
      );
      return response;
    } catch (error) {
      console.error("❌ Get user preferences API error:", error);
      throw error;
    }
  }

  async updateUserPreferences(
    data: UpdatePreferencesRequest
  ): Promise<UserPreferencesResponse> {
    try {
      console.log("📡 Updating user preferences...");
      const response = await apiClient.put<UserPreferencesResponse>(
        API_ENDPOINTS.UPDATE_PREFERENCES,
        data
      );
      return response;
    } catch (error) {
      console.error("❌ Update user preferences API error:", error);
      throw error;
    }
  }

  // 🎁 Rewards API Methods
  async getRewards(): Promise<RewardsResponse> {
    try {
      const response = await apiClient.get<RewardsResponse>(
        API_ENDPOINTS.getRewards
      );
      return response;
    } catch (error) {
      console.error("❌ Get rewards API error:", error);
      throw error;
    }
  }

  async getRewardsHistory(
    params: RewardHistoryParams = {}
  ): Promise<RewardHistoryResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (params.page !== undefined)
        queryParams.append("page", String(params.page));
      if (params.limit !== undefined)
        queryParams.append("limit", String(params.limit));

      const url = `${API_ENDPOINTS.getRewardsHistory}${
        queryParams.toString() ? `?${queryParams.toString()}` : ""
      }`;

      const response = await apiClient.get<RewardHistoryResponse>(url);
      return response;
    } catch (error) {
      console.error("❌ Get rewards history API error:", error);
      throw error;
    }
  }

  async convertRewards(
    payload: ConvertRewardsRequest
  ): Promise<ConvertRewardsResponse> {
    try {
      const response = await apiClient.post<ConvertRewardsResponse>(
        API_ENDPOINTS.convertRewards,
        payload
      );
      return response;
    } catch (error) {
      console.error("❌ Convert rewards API error:", error);
      throw error;
    }
  }

  async previewConvertRewards(
    payload: PreviewConvertRewardsRequest
  ): Promise<PreviewConvertRewardsResponse> {
    try {
      const response = await apiClient.post<PreviewConvertRewardsResponse>(
        API_ENDPOINTS.previewConvertRewards,
        payload
      );
      return response;
    } catch (error) {
      console.error("❌ Preview convert rewards API error:", error);
      throw error;
    }
  }

  // 🛫 Cosmopoints API Methods
  async getCosmopointsPrograms(): Promise<CosmopointsProgramListResponse> {
    try {
      const response = await apiClient.get<CosmopointsProgramListResponse>(
        API_ENDPOINTS.getCosmopointsPrograms
      );
      return response;
    } catch (error) {
      console.error("❌ Get cosmopoints programs API error:", error);
      throw error;
    }
  }

  async getCosmopointsConnections(): Promise<CosmopointsConnectionListResponse> {
    try {
      const response = await apiClient.get<CosmopointsConnectionListResponse>(
        API_ENDPOINTS.getCosmopointsConnections
      );
      return response;
    } catch (error) {
      console.error("❌ Get cosmopoints connections API error:", error);
      throw error;
    }
  }

  async createCosmopointsConnection(
    payload: CreateCosmopointsConnectionRequest
  ): Promise<CosmopointsConnectionResponse> {
    try {
      const response = await apiClient.post<CosmopointsConnectionResponse>(
        API_ENDPOINTS.createCosmopointsConnection,
        payload
      );
      return response;
    } catch (error) {
      console.error("❌ Create cosmopoints connection API error:", error);
      throw error;
    }
  }

  async deleteCosmopointsConnection(id: string): Promise<void> {
    try {
      await apiClient.delete(API_ENDPOINTS.deleteCosmopointsConnection(id));
    } catch (error) {
      console.error("❌ Delete cosmopoints connection API error:", error);
      throw error;
    }
  }
}

export const queryService = new QueryService();
